/* ============================================================
   ITEM KINDS — the per-kind behaviour for a queue segment.
   A QueueItem is plain serializable DATA (`kind` + fields); its
   BEHAVIOUR lives here, one adapter per kind, exactly like the
   bump-scene / channel-component / score registries. The engine
   asks the adapter for an item's duration, title, how to present
   itself at an offset, and its transport capabilities — so adding
   a kind is adding an adapter, not editing a dozen switches.

   `role` (program vs break) is ORTHOGONAL to kind and lives on the
   item; the helpers at the bottom read it.
   ============================================================ */
import type { QueueItem, ItemRole } from './types'
import type { DesiredFrame } from './live'
import { itemContentDuration } from './portions'
import { getSceneDurationMs } from '@/bumps/durations'

export interface ItemKind<T extends QueueItem> {
  /** scheduled length in seconds (Infinity allowed — an "always on" component). */
  duration(item: T): number
  /** display title for the guide / OSD. */
  title(item: T): string
  /** how the item presents itself at `atSec` within it (the engine renders the returned frame). */
  frame(item: T, index: number, atSec: number, track: boolean): DesiredFrame
  /** can the viewer play/pause it? (a video — yes; a scene/component runs on the broadcast clock). */
  controllable: boolean
  /** does the engine live-sync it by SEEKING to an offset? (video — yes; a scene fast-forwards via
   *  its frame's startSec, a component ignores the offset). */
  seekable: boolean
  /** the pre-buffer key (a video iframe id), or null when there's nothing to pre-load. */
  bufferId(item: T): string | null
}

type ItemOfKind<K extends QueueItem['kind']> = Extract<QueueItem, { kind: K }>

const KINDS: { [K in QueueItem['kind']]: ItemKind<ItemOfKind<K>> } = {
  video: {
    duration: (i) => itemContentDuration(i),
    title: (i) => i.title,
    frame: (i, index, atSec, track) => ({ kind: 'video', id: i.videoId, atSec, index, track, portions: i.portions ?? [] }),
    controllable: true,
    seekable: true,
    bufferId: (i) => i.videoId,
  },
  scene: {
    duration: (i) => getSceneDurationMs(i.scene) / 1000,
    title: (i) => i.title,
    frame: (i, index, atSec) => ({ kind: 'scene', scene: i.scene, params: i.params, atSec, index }),
    controllable: false, // a scene runs on its own clock — no pause, no seek
    seekable: false,
    bufferId: () => null,
  },
  component: {
    // absent durationSeconds → Infinity: an "always on" interactive segment (e.g. HIDEO) that owns
    // the whole non-wrapping timeline. A finite durationSeconds lets it air as a normal timed segment.
    duration: (i) => i.durationSeconds ?? Infinity,
    title: (i) => i.title,
    frame: (i, index, atSec) => ({ kind: 'component', component: i.component, atSec, index }),
    controllable: false, // the component drives itself (its own interactivity) — no transport
    seekable: false,
    bufferId: () => null,
  },
}

/** The adapter for an item's kind (a deliberate widening at the registry boundary). */
const kindOf = (item: QueueItem): ItemKind<QueueItem> => KINDS[item.kind] as ItemKind<QueueItem>

export const itemDurationSeconds = (item: QueueItem): number => kindOf(item).duration(item)
export const itemTitle = (item: QueueItem): string => kindOf(item).title(item)
export const itemControllable = (item: QueueItem): boolean => kindOf(item).controllable
export const itemSeekable = (item: QueueItem): boolean => kindOf(item).seekable
export const itemBufferId = (item: QueueItem): string | null => kindOf(item).bufferId(item)

/** The desired frame for a queue position + offset, dispatched to the item's adapter. */
export function frameFor(queue: QueueItem[], index: number, atSec: number, track = false): DesiredFrame {
  const item = queue[index]
  if (!item) return { kind: 'off' }
  return kindOf(item).frame(item, index, Math.max(0, atSec), track)
}

// --- role (orthogonal to kind) ---
export const itemRole = (item: QueueItem): ItemRole => item.role ?? 'program'
/** A program is a listed show (any kind); a break is injected filler. The guide / ordinals count these. */
export const isProgram = (item: QueueItem): boolean => itemRole(item) !== 'break'
